/**
 * Links into the app wait for the signed release list.
 *
 * The page shows a link to the app only after `verify.ts` accepts the list.
 * Some of those links live in the page's fixed frame, outside the area that
 * `render.ts` redraws (the top bar docs links and the footer). Mark such an element with
 * {@link VERIFIED_LINK_ATTRIBUTE} and keep it hidden. The page calls
 * {@link showVerifiedLinks} when the list verifies.
 */

/** The attribute that marks an element as a link into the app. */
export const VERIFIED_LINK_ATTRIBUTE = 'data-needs-verified'

/** Shows or hides every marked element under `scope`. */
export const showVerifiedLinks = (scope: ParentNode, options: { visible: boolean }): void => {
  for (const element of scope.querySelectorAll<HTMLElement>(`[${VERIFIED_LINK_ATTRIBUTE}]`)) {
    element.hidden = !options.visible
  }
}

/**
 * The attribute that marks a link into the verified release's own files. Its
 * value is the path under the release root, such as `docs/guide/`. The link
 * has no address until the list verifies, then {@link pointReleaseLinks} sets it.
 */
export const RELEASE_PATH_ATTRIBUTE = 'data-release-path'

/**
 * Points every marked link under `scope` into the release at `releaseOrigin`
 * (the release's own content-address origin, ending in a slash). The page
 * never links the shared `latest` origin: it is a separate app, not advertised here.
 */
export const pointReleaseLinks = (scope: ParentNode, releaseOrigin: string): void => {
  for (const link of scope.querySelectorAll<HTMLAnchorElement>(`[${RELEASE_PATH_ATTRIBUTE}]`)) {
    link.href = `${releaseOrigin}${link.getAttribute(RELEASE_PATH_ATTRIBUTE) ?? ''}`
  }
}
